import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { RubyPlatformProvider, RubyWidget } from "@attmos/ruby-ui";

import { useSession } from "@/api/auth";
import { Footer } from "@/components/layout/Footer";
import { Sidebar } from "@/components/layout/Sidebar";
import { SkipToContent } from "@/components/layout/SkipToContent";
import { RunCommandPalette } from "@/components/RunCommandPalette";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { createFlowStackAdapter } from "@/ruby/adapter";

/**
 * App shell — a full-height left rail with the workspace column
 * (content outlet + © line) beside it.
 *
 * **The outer axis is a ROW** (flowstack#953). It used to be a column
 * — Header, then a row of [Sidebar, main], then Footer — which boxed
 * the rail between two edge-to-edge bars. Now the rail is the first
 * thing on the page and spans the viewport top to bottom.
 *
 * **And there is no top bar at all** (flowstack#1002). The rail is the
 * app's only chrome: its three occupants — the lockup, the collapse
 * toggle and the ⌘K trigger — moved into the rail's own top and bottom
 * zones, so ``<main>`` now starts at y = 0 and every page gets the
 * 56px back. Page-specific verbs belong to the page's own top-right
 * row, not to global chrome; nothing here blocks one.
 *
 * Wires the global cmd-K palette + sonner toaster + tooltip provider
 * so every route gets them for free.
 *
 * **A route change swaps the page at once — there is no route
 * transition** (fs#1868). It used to be a 120ms `AnimatePresence`
 * crossfade in `mode="wait"`, which holds the new page back until the
 * old one's exit fade reports finished. Two ways that went wrong:
 *
 * - With a live `<Outlet />` in the leaving wrapper, the NEW page
 *   mounted there during the fade, was thrown away, and mounted again
 *   after it — so a ligand picked on Submit Flow within the fade
 *   vanished (fs#1801).
 * - #1858 fixed that by capturing the page with `useOutlet()`, which
 *   made the leaving wrapper keep the OLD page. That is right while the
 *   fade runs, but it made the page swap depend on the animation clock:
 *   in a tab whose animations do not advance (backgrounded, throttled,
 *   or driven by an automation browser) the exit never finishes, and
 *   the URL changed while the old page stayed on screen for good —
 *   clicking a Submit chooser card showed the chooser forever.
 *
 * Nothing that decides WHICH page is on screen may wait on an
 * animation. The wrapper stays keyed by pathname, so a new path is
 * still a fresh mount, exactly as before; `submit_chooser.spec.ts`
 * pins the swap with the browser's animations frozen.
 */
export function AppShell(): JSX.Element {
  const location = useLocation();
  const { data: session } = useSession();
  const adapter = useMemo(() => createFlowStackAdapter(session ?? null), [session]);

  return (
    <RubyPlatformProvider adapter={adapter}>
      <TooltipProvider delayDuration={200}>
        <div className="flex h-screen w-screen text-foreground">
          {/* FIRST FOCUSABLE ELEMENT IN THE DOCUMENT, and it has to stay
              that way (flowstack#983): a skip link placed after the rail
              is a skip link the user tabs 13 stops to reach. It is
              `position: fixed`, so being a child of this row costs the row
              nothing in either state. */}
          <SkipToContent />
          {/* Top to bottom, and FIRST — the rail is a child of the row,
              so it stretches to the full ``h-screen`` height rather
              than to whatever is left between two bars. */}
          <Sidebar />
          {/* The workspace column. ``overflow-hidden`` also resolves
              this column's automatic minimum WIDTH to 0, which is what
              lets a wide table or a DAG canvas inside <main> shrink
              instead of pushing the rail off-screen. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* ``<main>`` is a plain block + overflow-y-auto. An earlier
                draft made IT a flex column so a nested Footer would push
                to the bottom — Rupert PR-334 r2 #1 flagged that as a
                regression risk (flex items have ``min-height: auto``
                which can prevent the scroll container from clamping,
                breaking ``h-full`` tables / split panes / 3D viewer
                canvases). That still holds, and it now has live
                dependants: the run workspace's split panel (#956) and
                the molecule viewer (#946) both need this clamp.

                #953 moved <main> from a row flex item to a COLUMN one,
                which is the axis where ``min-height: auto`` actually
                bites, so the clamp is now spelled out with ``min-h-0``
                rather than left to the (correct but implicit) rule that
                a non-``visible`` overflow already zeroes it. <main>
                itself stays ``display: block`` — the constraint above
                is about <main>'s OWN display, and the Footer reaches the
                bottom as this column's last child, not by making <main>
                a flex parent.

                `tabIndex={-1}` is what makes the skip link work rather
                than merely exist (#983). Without it `main.focus()` is a
                no-op, the next Tab resumes from the rail, and the link
                moves the scroll position while a screen reader is still
                sitting in the chrome. `-1` keeps it OUT of the tab order
                — it is a landmark, not a stop. */}
            <main className="min-h-0 flex-1 overflow-y-auto" id="main" tabIndex={-1}>
              <ErrorBoundary>
                <div
                  key={location.pathname}
                  /* The page gutter, and the app's only one. ``p-gutter``
                     resolves to ``--gutter``. It sits INSIDE <main>, on
                     the page wrapper: <main>'s own box is what
                     ``app_frame.spec.ts`` measures against the rail, and
                     padding here cannot reach it.

                     The bottom is the gutter PLUS ``--assistant-clearance``
                     (globals.css carries the arithmetic): the assistant
                     widget is fixed over the page's bottom-right corner, so
                     a page whose last row is a right-aligned action —
                     "Submit Flow" — puts that action under it and it stops
                     being clickable. Both halves are tokens, so the bottom
                     inset still moves with density. */
                  className="p-gutter pb-[calc(var(--gutter)+var(--assistant-clearance))]"
                >
                  <Outlet />
                </div>
              </ErrorBoundary>
            </main>
            {/* Footer sits OUTSIDE <main> so the browser exposes it as
                the page-level ``contentinfo`` landmark (a <footer>
                nested in <main> is scoped to that main and never
                reaches ``contentinfo``). Rupert PR-334 r2 #2.

                It moved INSIDE the workspace column in #953 so it stops
                spanning under the rail — and that keeps the landmark,
                because ``<footer>`` only loses ``contentinfo`` when it
                descends from a sectioning element (article / aside /
                main / nav / section). Everything between it and <body>
                here is a plain <div>, and it is still a SIBLING of
                <main>. `AppShell.test.tsx` pins both halves. */}
            <Footer />
          </div>
          {/* Overlays, not layout: the palette portals when open and
              renders nothing when closed, the toaster's list and the
              Ruby widget are ``position: fixed``. None of them is an
              in-flow child of the row above, so none takes width from
              the workspace. */}
          <RunCommandPalette />
          <Toaster />
          <ErrorBoundary>
            <RubyWidget currentPath={location.pathname} />
          </ErrorBoundary>
        </div>
      </TooltipProvider>
    </RubyPlatformProvider>
  );
}
